import { AuthSessionMissingError, isAuthWeakPasswordError, type User as SupabaseUser } from '@supabase/supabase-js';

import { client, invokeWithTimeout } from './client';
import { Gender, MinimalUser, Role, User, CreateUser, UpdateUser, LoginUser } from '../../models/User';
import { toMinimalUser } from './profileMappers';
import { uploadProfilePicture } from './imageService';
import { errorCode } from './errorCode';
import { logError } from '../../utils/logger';
import { RegisterError } from '../../error/RegisterError';
import { MIN_USER_SEARCH_QUERY_LENGTH, SEARCH_RESULT_LIMIT } from './constants';
import { throwIfSupabaseError } from './throwIfSupabaseError';

const table = 'user_profiles';

export async function refreshUser(): Promise<SafeUser | undefined> {
    const { data, error } = await client.auth.getUser();

    // AuthSessionMissingError is the expected "no one's logged in" signal —
    // surface as `undefined`, don't ship to Sentry. Other auth errors are
    // logged then swallowed (we still try to read the cached profile below).
    if (error) {
        if (!(error instanceof AuthSessionMissingError)) {
            logError(error);
        }

        return undefined;
    }

    try {
        await postLogin(data.user);
        const profile = await findUser(data.user.id);

        if (profile == null) {
            throw new Error('No such user');
        }

        return profile;
    } catch (error) {
        logError(error);
        return await findUser(data.user.id);
    }
}

export async function createUser(payload: CreateUser): Promise<void> {
    try {
        const response = await client.auth.signUp({
            email: payload.email,
            password: payload.password,
            options: {
                // `preferred_gender` rides along in auth metadata so
                // `createUserProfile` can seed the `user_preferences` row on
                // first login. Omitted when the caller didn't supply one —
                // the preferences default (`'male'`) then takes over.
                data: {
                    username: payload.username,
                    discord_username: payload.discordUsername,
                    role: Role.Player,
                    ...(payload.preferredGender != null
                        ? { preferred_gender: payload.preferredGender }
                        : {}),
                },
            },
        });

        throwIfSupabaseError(response);
    } catch (error) {
        if (!isAuthWeakPasswordError(error)) {
            logError(error);
            throw error;
        } else {
            throw new RegisterError('Lozinka je kompromitirana ili preslaba.');
        }
    }
}

export async function sendPasswordReset(email: string): Promise<void> {
    const response = await client.auth.resetPasswordForEmail(email, {
        redirectTo: 'https://odg.hr/promijeni-lozinku',
    });
    throwIfSupabaseError(response);
}

type UsernameAuthResponse = {
    user: SupabaseUser;
    session: { access_token: string; refresh_token: string };
};

async function loginWithUsernameOrEmail(payload: LoginUser): Promise<{ user?: SupabaseUser }> {
    const { data, error } = await invokeWithTimeout<UsernameAuthResponse>('username-auth', {
        body: {
            identifier: payload.email,
            password: payload.password,
        },
    });

    // Defensive throw on missing data even when there's no error — the edge
    // function is supposed to return a session shape, and the rest of the
    // login flow would crash on the implicit undefined.
    if (error || !data) {
        throw error ?? new Error('username-auth returned no data');
    }

    const { access_token, refresh_token } = data.session;
    await client.auth.setSession({ access_token, refresh_token });

    return { user: data.user };
}

export async function areUsernamesTaken(
    username: string,
    discordUsername?: string,
): Promise<boolean> {
    const query = discordUsername != null && !!discordUsername.trim().length
        ? `username.eq.${username},discord_username.eq.${discordUsername}` as const
        : `username.eq.${username}` as const;

    const response = await client
        .from(table)
        .select('id', { count: 'exact', head: true })
        .or(query);
    throwIfSupabaseError(response);

    return (response.count ?? 0) > 0;
}

async function getOrCreateProfile(baseUser: SupabaseUser): Promise<SafeUser | undefined> {
    const discordUsername = baseUser.user_metadata?.custom_claims?.discord_username ?? baseUser.user_metadata?.custom_claims?.global_name;
    const username = baseUser.user_metadata?.username ?? discordUsername;
    const preferredGender = readPreferredGenderFromMetadata(baseUser);

    // Existence check folded into the full profile fetch: if the row exists,
    // return it directly; otherwise fall through to the create path. One
    // round-trip instead of an existence-count + a follow-up findUser.
    const existing = await findUser(baseUser.id);

    if (existing != null) {
        return existing;
    }

    if (!username) {
        const error = new Error('Cannot create profile: no username available from auth metadata');
        logError(error);
        throw error;
    }

    if (await areUsernamesTaken(username, discordUsername)) {
        logError(new Error(`Username or Discord username already taken: ${username} / ${discordUsername}`));
        throw new Error('Username or Discord username already taken');
    }

    return await createUserProfile(baseUser.id, {
        username,
        discordUsername,
        preferredGender,
    });
}

function readPreferredGenderFromMetadata(baseUser: SupabaseUser): Gender | undefined {
    const raw = baseUser.user_metadata?.preferred_gender;
    if (raw === Gender.Male || raw === Gender.Female) {
        return raw;
    }
    return undefined;
}

export async function login(payload: LoginUser): Promise<SafeUser> {
    const data = await loginWithUsernameOrEmail(payload);

    if (data.user == null) {
        throw new Error(errorCode.NotFound);
    }

    const baseUser = data.user;

    // If the user doesn't exist in the user_profiles table, create a new profile
    const profile = await getOrCreateProfile(baseUser);

    if (profile == null) {
        throw new Error(errorCode.NotFound);
    }

    return profile;
}

// Post-WebAuthn-handshake profile bootstrap. The caller (UserService
// facade) drives the actual ceremony via `WebAuthnService` — by the
// time we get here, `client.auth.setSession` has already installed
// the session, and we just need to hydrate the local profile the same
// way the password and Discord paths do.
export async function loginWithPasskey(supabaseUser: SupabaseUser): Promise<SafeUser> {
    const profile = await getOrCreateProfile(supabaseUser);

    if (profile == null) {
        throw new Error(errorCode.NotFound);
    }

    return profile;
}

export async function loginWithDiscord(): Promise<void> {
    const signInResponse = await client.auth.signInWithOAuth({
        provider: 'discord',
        options: {
            redirectTo: 'https://odg.hr',
        },
    });

    if (signInResponse.error) {
        logError(signInResponse.error);
        throw signInResponse.error;
    }

    // Let's try to set up the discord username if not around
    try {
        const userResponse = await client.auth.getUser();
        if (userResponse.error || userResponse.data.user == null) {
            return;
        }

        const user = userResponse.data.user;

        const profile = await getOrCreateProfile(user);

        if (profile == null) {
            logError(new Error('Failed to get or create profile after Discord login'));
            return;
        }

        if (!profile.discordUsername) {
            const discordUsername = user.user_metadata?.custom_claims?.discord_username ?? user.user_metadata?.custom_claims?.global_name;

            if (discordUsername) {
                await updateUser({
                    id: user.id,
                    username: profile.username ?? discordUsername,
                    role: profile.role,
                    discordUsername,
                });
            }
        }
    } catch (error) {
        logError(error, { context: 'Failed to update discord username' });
    }
}

export async function logOut(): Promise<void> {
    await client.auth.signOut();
}

export async function updateUser(payload: UpdateUser, isAdminEditingAnotherUser = false): Promise<SafeUser> {
    try {
        const userResponse = await client.auth.getUser();
        throwIfSupabaseError(userResponse);

        if (userResponse.data.user == null) {
            throw new Error('updateUser called without an authenticated session');
        }

        if (payload.password) {
            const passwordUpdate = isAdminEditingAnotherUser
                ? await invokeWithTimeout('set-user-password', {
                    method: 'POST',
                    body: {
                        userId: payload.id,
                        password: payload.password,
                    }
                })
                : await client.auth.updateUser({
                    password: payload.password,
                });

            throwIfSupabaseError(passwordUpdate);
        }

        const dto: Omit<UserProfileDto, 'id' | 'role'> & { role?: Role } = {
            user_id: payload.id,
            image_url: undefined as string | undefined,
            username: payload.username,
            discord_username: payload.discordUsername,
        };

        // Only admins editing another user may set the role. RLS must enforce
        // the same — but we drop role from self-edits as a defense-in-depth so
        // that a tampered client cannot escalate its own role.
        if (isAdminEditingAnotherUser) {
            dto.role = payload.role;
        }

        if (payload.username) {
            dto.username = payload.username;
        }

        if (payload.avatar) {
            dto.image_url = await uploadProfilePicture(userResponse.data.user.id, payload.avatar);
        }

        const response = await client
            .from(table)
            .update(dto)
            .eq('user_id', payload.id)
            .select();
        throwIfSupabaseError(response);

        const profile = response.data[0] as UserProfileDto | undefined;
        if (profile == null) {
            throw new Error('updateUser succeeded but no row was returned');
        }

        // Source every field from the DB row, never from the payload — otherwise a
        // tampered self-edit could echo back e.g. role: 'Admin' and flip client-side
        // permission gates until the next refresh, even though RLS rejected the write.
        return {
            ...toMinimalUser(profile),
            fullName: profile.full_name,
        };
    } catch (error) {
        logError(error);
        throw error;
    }
}

export async function getAllUsers(): Promise<MinimalUser[]> {
    try {
        // Cap the result set so a future growth spike can't silently turn
        // the admin Users page / player picker into a megabyte download.
        // Today's table is well under this; revisit if it ever approaches.
        const response = await client
            .from(table)
            .select('user_id, full_name, username, image_url, discord_username, role')
            .order('username')
            .limit(5000);
        throwIfSupabaseError(response);

        return response.data.map((entry): MinimalUser & { fullName?: string } => ({
            ...toMinimalUser(entry),
            fullName: entry.full_name ?? undefined,
        }));
    } catch (error) {
        logError(error);
        throw error;
    }
}

export async function searchUsers(query: string): Promise<MinimalUser[]> {
    if (query.length < MIN_USER_SEARCH_QUERY_LENGTH) {
        return [];
    }

    const response = await client
        .from(table)
        .select('user_id, full_name, username, image_url, discord_username, role')
        .in('role', [Role.Admin, Role.Owner, Role.Bot, Role.GM])
        .ilike('username', `%${query}%`)
        .limit(SEARCH_RESULT_LIMIT);
    throwIfSupabaseError(response);

    return response.data.map((entry): MinimalUser & { fullName?: string } => ({
        ...toMinimalUser(entry),
        fullName: entry.full_name ?? undefined,
    }));
}

export async function getAdmins(): Promise<SafeUser[]> {
    const response = await client
        .from(table)
        .select('user_id, full_name, username, image_url, discord_username, role')
        .eq('role', Role.Admin);
    throwIfSupabaseError(response);

    return response.data.map((entry): SafeUser => ({
        ...toMinimalUser(entry),
        fullName: entry.full_name ?? undefined,
    }));
}

export async function findUsers(ids: Array<SafeUser['id']>): Promise<Record<SafeUser['id'], SafeUser>> {
    try {
        const response = await client
            .from(table)
            .select('user_id, username, full_name, image_url, role')
            .in('user_id', ids);
        throwIfSupabaseError(response);

        return response.data
            .map((entry): SafeUser => ({
                ...toMinimalUser(entry),
                fullName: entry.full_name ?? undefined,
            }))
            .reduce(
                (acc, user) => {
                    acc[user.id] = user;
                    return acc;
                },
                {} as Record<SafeUser['id'], SafeUser>,
            );
    } catch (error) {
        logError(error);
        throw error;
    }
}

export async function findUser(id: SafeUser['id']): Promise<SafeUser | undefined> {
    const response = await client
        .from(table)
        .select('user_id, username, full_name, image_url, role, discord_username')
        .eq('user_id', id)
        .limit(1)
        .maybeSingle();
    throwIfSupabaseError(response);

    const data = response.data;
    if (data == null) {
        return undefined;
    }

    return {
        ...toMinimalUser(data),
        fullName: data.full_name ?? undefined,
    };
}

async function createUserProfile(
    userId: SafeUser['id'],
    payload: Omit<CreateUser, 'password' | 'email'>,
): Promise<SafeUser> {
    const profileResponse = await client
        .from(table)
        .insert({
            user_id: userId,
            username: payload.username,
        })
        .select();
    throwIfSupabaseError(profileResponse);

    const profile = profileResponse.data[0] as UserProfileDto;

    if (payload.preferredGender != null) {
        // Best-effort seed — the DB column has a default so a failure here
        // doesn't block sign-up. The user can still flip it in Settings.
        const { error: prefsError } = await client
            .from('user_preferences')
            .upsert({
                user_id: userId,
                preferred_gender: payload.preferredGender,
            });

        if (prefsError) {
            logError(prefsError, { context: 'Failed to seed preferred_gender on first profile creation' });
        }
    }

    return {
        ...toMinimalUser({ ...profile, user_id: userId }),
        fullName: profile.full_name ?? undefined,
    };
}

async function postLogin(user: SupabaseUser): Promise<void> {
    try {
        const profile = await getOrCreateProfile(user);

        if (profile == null) {
            return;
        }

        if (profile.discordUsername && profile.username) {
            return;
        }

        const discordUsername = user.user_metadata?.custom_claims?.discord_username ?? user.user_metadata?.custom_claims?.global_name;

        const update: UpdateUser = {
            id: user.id,
            username: profile.username ?? discordUsername,
            role: profile.role,
            discordUsername: profile.discordUsername ?? discordUsername,
        };

        await updateUser(update);
    } catch (error) {
        logError(error, { context: 'Failed to run post-login user upgrade from Discord metadata' });
    }
}

type SafeUser = Omit<User, 'email'>;

type UserProfileDto = {
    id: number;
    user_id: string;
    full_name?: string;
    username?: string;
    image_url?: string;
    discord_username?: string;
    role: Role;
};
